import { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import JSZip from 'jszip';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Loader2, X, ChevronDown, Image, Package, Settings2, Upload } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { renderViewToDataURL } from '@/lib/canvas-export';
import { pushCADToPortal } from '@/utils/api';
import { CADSheetBody } from './CADSheetBody';

const VISIBILITY_OPTIONS = [
  { key: 'header', label: 'Header (Style, Metal, Weight)' },
  { key: 'images', label: 'Image Grid' },
  { key: 'stones', label: 'Stone Details Table' },
  { key: 'findings', label: 'Findings' },
  { key: 'finishing', label: 'Finishing' },
  { key: 'engraving', label: 'Engraving' },
  { key: 'customerImages', label: 'Customer Reference Images' },
  { key: 'notes', label: 'Notes / Other Details' },
];

export function LivePreview({ data, open, onClose, taskId = null }) {
  const previewRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [showVisibility, setShowVisibility] = useState(false);
  const [visibility, setVisibility] = useState(() => {
    const defaults = {};
    VISIBILITY_OPTIONS.forEach(o => { defaults[o.key] = true; });
    return defaults;
  });

  const toggleVisibility = (key) => {
    setVisibility(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDownloadConsolidated = async () => {
    if (!previewRef.current || exporting) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(previewRef.current, { pixelRatio: 3, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `${data.styleNo || 'CAD-Sheet'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!previewRef.current || exporting) return;
    setExporting(true);
    try {
      const zip = new JSZip();
      const prefix = data.styleNo || 'CAD-Sheet';
      const metadata = {
        partyDesignNo: data.customerDesignNo || '',
        styleNo: data.styleNo || '',
        pieceName: data.jewelryType || '',
      };
      for (let i = 0; i < data.images.length; i++) {
        const img = data.images[i];
        if (!img.url) continue;
        try {
          const viewDataUrl = await renderViewToDataURL(img, metadata);
          const base64 = viewDataUrl.split(',')[1];
          zip.file(`${prefix}_${img.label.replace(/[^a-zA-Z0-9]/g, '_')}.png`, base64, { base64: true });
        } catch (e) { console.error(`Failed to export view ${img.label}:`, e); }
      }
      const consolidatedUrl = await toPng(previewRef.current, { pixelRatio: 3, backgroundColor: '#ffffff' });
      const consolidatedBase64 = consolidatedUrl.split(',')[1];
      zip.file(`${prefix}_Consolidated.png`, consolidatedBase64, { base64: true });
      const blob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.download = `${prefix}_Views.zip`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('ZIP with all views + consolidated sheet downloaded.');
    } catch (err) {
      console.error('ZIP export failed:', err);
      toast.error('Could not generate ZIP file.');
    } finally {
      setExporting(false);
    }
  };

  // Reverse upload: send the same artifacts (consolidated + per-view PNGs) to
  // the SGI portal as CAD images on the source order. Mirrors handleDownloadZip
  // so the customer sees the same set of images they'd see if we ZIP-exported.
  const handlePushToPortal = async () => {
    if (!previewRef.current || pushing || exporting) return;
    if (!taskId && !data.styleNo && !data.customerDesignNo) {
      toast.error('No linked task — cannot determine which SGI order to push to.');
      return;
    }
    setPushing(true);
    try {
      const prefix = (data.styleNo || data.customerDesignNo || 'CAD-Sheet').replace(/[^a-zA-Z0-9._-]/g, '_');
      const metadata = {
        partyDesignNo: data.customerDesignNo || '',
        styleNo: data.styleNo || '',
        pieceName: data.jewelryType || '',
      };

      const images = [];
      // Per-view annotated PNGs
      for (let i = 0; i < data.images.length; i++) {
        const img = data.images[i];
        if (!img.url) continue;
        try {
          const viewDataUrl = await renderViewToDataURL(img, metadata);
          const safeLabel = (img.label || `view-${i + 1}`).replace(/[^a-zA-Z0-9]/g, '_');
          images.push({
            data_url: viewDataUrl,
            filename: `${prefix}_${safeLabel}.png`,
            notes: `View: ${img.label || ''}`.trim(),
          });
        } catch (e) {
          console.error(`Failed to render view ${img.label}:`, e);
        }
      }
      // Consolidated full-sheet PNG
      const consolidatedUrl = await toPng(previewRef.current, { pixelRatio: 3, backgroundColor: '#ffffff' });
      images.push({
        data_url: consolidatedUrl,
        filename: `${prefix}_Consolidated.png`,
        notes: 'Consolidated CAD sheet (all views + specs)',
      });

      if (images.length === 0) {
        toast.error('Nothing to upload — sheet has no images.');
        return;
      }

      toast.info(`Uploading ${images.length} image(s) to SGI portal…`);
      const res = await pushCADToPortal({
        task_id: taskId || undefined,
        style_no: data.styleNo || undefined,
        customer_design_no: data.customerDesignNo || undefined,
        images,
      });

      const uploaded = res?.data?.uploaded ?? res?.uploaded ?? 0;
      const total = res?.data?.total ?? res?.total ?? images.length;
      const ok = res?.data?.success ?? res?.success;
      // Surface the first per-file failure message from the uploader so the
      // operator sees the actual reason (login failed / HTTP 500 / portal
      // rejected file / network error) rather than a generic "failed" toast.
      const firstFail = (res?.data?.results || res?.results || []).find(r => !r.success);
      const errSummary = res?.data?.error || res?.error || firstFail?.message || 'unknown reason';
      if (ok && uploaded === total) {
        toast.success(`Pushed ${uploaded} image(s) to SGI portal.`);
      } else if (uploaded > 0) {
        toast.warning(`Pushed ${uploaded} of ${total} image(s) — ${errSummary}`, { duration: 10000 });
      } else {
        toast.error(`SGI upload failed: ${errSummary}`, { duration: 12000 });
      }
    } catch (err) {
      console.error('Push to SGI failed:', err);
      const msg = err?.response?.data?.detail || err?.message || 'unknown error';
      toast.error(`Could not push to SGI: ${msg}`);
    } finally {
      setPushing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/98 backdrop-blur-sm flex flex-col">
      <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-card">
        <div className="flex items-center gap-2">
          <img src="/ks-logo.png" alt="K's" className="h-6 w-6 object-contain" />
          <h2 className="text-lg font-semibold text-primary">Sheet Preview</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={showVisibility ? 'default' : 'outline'}
            onClick={() => setShowVisibility(!showVisibility)}
            className="h-8"
            data-testid="cad-visibility-toggle"
          >
            <Settings2 className="h-3 w-3 mr-1" />Visibility
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" disabled={exporting || pushing} className="h-8 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="cad-export-btn">
                {(exporting || pushing) ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}Export<ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDownloadConsolidated}><Image className="h-4 w-4 mr-2" />Consolidated PNG</DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadZip}><Package className="h-4 w-4 mr-2" />Views + Consolidated (ZIP)</DropdownMenuItem>
              <DropdownMenuItem onClick={handlePushToPortal} data-testid="cad-push-sgi"><Upload className="h-4 w-4 mr-2" />Push to SGI Portal</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8"><X className="h-4 w-4" /></Button>
        </div>
      </div>

      {showVisibility && (
        <div className="px-6 py-3 border-b border-border bg-muted/30 flex flex-wrap items-center gap-4" data-testid="cad-visibility-panel">
          <span className="text-xs font-semibold text-muted-foreground mr-2">Show in output:</span>
          {VISIBILITY_OPTIONS.map(opt => (
            <label key={opt.key} className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                checked={visibility[opt.key]}
                onCheckedChange={() => toggleVisibility(opt.key)}
                data-testid={`cad-vis-${opt.key}`}
              />
              <span className="text-xs text-foreground">{opt.label}</span>
            </label>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-auto flex justify-center p-8 bg-muted/30">
        <CADSheetBody ref={previewRef} data={data} visibility={visibility} showCompanyFooter withShadow />
      </div>
    </div>
  );
}
